import { createClient } from "@supabase/supabase-js";
import {
  supabasePublishableKey,
  supabaseUrl,
} from "./supabaseClient";

export const PASSWORD_RECOVERY_STORAGE_KEY =
  "pagebunny-password-recovery-auth";
export const PASSWORD_VERIFICATION_STORAGE_KEY =
  "pagebunny-password-verification-auth";

const recoveryCodeVerifierKey =
  `${PASSWORD_RECOVERY_STORAGE_KEY}-code-verifier`;
const memoryStorage = new Map<string, string>();

function getBrowserStorage(key: string): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  return key === recoveryCodeVerifierKey
    ? window.localStorage
    : window.sessionStorage;
}

const passwordRecoveryStorage = {
  getItem(key: string): string | null {
    try {
      const browserStorage = getBrowserStorage(key);
      return browserStorage
        ? browserStorage.getItem(key)
        : memoryStorage.get(key) ?? null;
    } catch {
      return memoryStorage.get(key) ?? null;
    }
  },
  setItem(key: string, value: string): void {
    memoryStorage.set(key, value);
    try {
      getBrowserStorage(key)?.setItem(key, value);
    } catch {
      // The in-memory fallback still keeps the isolated flow usable in this tab.
    }
  },
  removeItem(key: string): void {
    memoryStorage.delete(key);
    try {
      getBrowserStorage(key)?.removeItem(key);
    } catch {
      // The in-memory copy is already cleared.
    }
  },
};

export function clearPasswordRecoveryStorage(): void {
  memoryStorage.delete(PASSWORD_RECOVERY_STORAGE_KEY);
  memoryStorage.delete(recoveryCodeVerifierKey);

  if (typeof window === "undefined") {
    return;
  }

  for (const storageName of ["localStorage", "sessionStorage"] as const) {
    try {
      const storage = window[storageName];
      storage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
      storage.removeItem(recoveryCodeVerifierKey);
    } catch {
      // Keep clearing the other isolated storage location when one is blocked.
    }
  }
}

export const passwordRecoveryClient = createClient(
  supabaseUrl,
  supabasePublishableKey,
  {
    auth: {
      storageKey: PASSWORD_RECOVERY_STORAGE_KEY,
      storage: passwordRecoveryStorage,
      detectSessionInUrl: false,
      flowType: "pkce",
      persistSession: true,
      autoRefreshToken: false,
    },
  }
);

export const passwordVerificationClient = createClient(
  supabaseUrl,
  supabasePublishableKey,
  {
    auth: {
      storageKey: PASSWORD_VERIFICATION_STORAGE_KEY,
      detectSessionInUrl: false,
      flowType: "pkce",
      persistSession: false,
      autoRefreshToken: false,
    },
  }
);
